import React from 'react';
import PropTypes from 'prop-types';

/**
 * ActionButton Component
 * 
 * Pre-defined button types for common actions:
 * - cancelCheckIn: "Cancel check-in" button (accent color)
 * - checkIn: "Check in" button (primary color)
 * - upload: Upload/submit buttons (primary color)
 * - cancel: Generic cancel buttons (accent color)
 *
 * The first two are about arriving on the day (check-in), not the RSVP answer.
 */
const ActionButton = ({ type, onClick, disabled, isLoading, children, ...rest }) => {
  const baseClasses = 'inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed shadow-sm h-7';

  const configs = {
    cancelCheckIn: {
      style: {
        backgroundColor: 'var(--color-neutral)',
        color: 'var(--color-secondary)',
        borderColor: 'var(--color-neutral)',
        borderWidth: '1px',
        borderStyle: 'solid',
      },
      icon: (
        <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      ),
      loadingIcon: (
        <svg className="w-3 h-3 mr-1 flex-shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ),
      text: 'Cancel check-in',
      loadingText: 'Cancelling...',
    },
    checkIn: {
      style: {
        backgroundColor: 'var(--color-primary)',
        color: 'var(--color-surface)',
        borderColor: 'var(--color-primary)',
        borderWidth: '1px',
        borderStyle: 'solid',
      },
      icon: (
        <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      ),
      loadingIcon: (
        <svg className="w-3 h-3 mr-1 flex-shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ),
      text: 'Check in',
      loadingText: 'Checking in...',
    },
    upload: {
      style: {
        backgroundColor: 'var(--color-primary)',
        color: 'var(--color-surface)',
        borderColor: 'var(--color-primary)',
        borderWidth: '1px',
        borderStyle: 'solid',
      },
      icon: null,
      text: children || 'Upload',
    },
    cancel: {
      style: {
        backgroundColor: 'var(--color-accent)',
        color: 'var(--color-secondary)',
        borderColor: 'var(--color-accent)',
        borderWidth: '1px',
        borderStyle: 'solid',
      },
      icon: null,
      text: children || 'Cancel',
    },
  };

  const config = configs[type];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isLoading}
      className={baseClasses}
      style={config.style}
      {...rest}
    >
      {isLoading ? config.loadingIcon : config.icon}
      {isLoading && config.loadingText ? config.loadingText : (children || config.text)}
    </button>
  );
};

ActionButton.propTypes = {
  type: PropTypes.oneOf(['cancelCheckIn', 'checkIn', 'upload', 'cancel']).isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  children: PropTypes.node,
};

export default ActionButton;
